/* eslint-disable no-unused-vars */
import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { RegisterDesignConfig } from '../../Hooks/Auth/useBrandAuthConfig';
import useTailwindBrand from '../../Theme/useTailwindBrand';
import iconGoogle from '../../Assets/Images/google_icon.svg'
import { userSetCurrentRegister } from '../../Redux/Actions/UserActions';
import FormSeparator from '../../Components/FormFields/FormSeparator';
import ButtonField from '../../Components/Ui/ButtonField';

interface LayoutStepsProps {
    designConfig: RegisterDesignConfig;
    loading?: boolean;
    handleGoogleSignUp?: () => Promise<any>;
    handleSubmit?: ((e: React.FormEvent) => Promise<void>) | ((event: React.FormEvent) => void)
    children: React.ReactElement;
    text?: string;
    btnText?: string;
    stepNumber?: number;
    /** Espace client : titres adaptés au client et parcours en 2 étapes. */
    isClient?: boolean;
    totalSteps?: number;
}

const LayoutSteps = ({
    designConfig,
    loading,
    handleGoogleSignUp,
    handleSubmit,
    children,
    text,
    btnText,
    stepNumber,
    isClient = false,
    totalSteps = 3
}: LayoutStepsProps) => {
    const { classes, common, brandConfig } = useTailwindBrand();
    const dispatch = useDispatch();
    const keyNumber = parseInt(children.key as string)
    const navigate = useNavigate()
    const primary = brandConfig.colors?.primary ?? '#0054A3';
    const currentStep = stepNumber ?? keyNumber ?? 1;

    // Mettre à jour le currentRegister dans Redux quand stepNumber change
    useEffect(() => {
        if (keyNumber) {
            dispatch(userSetCurrentRegister(keyNumber));
        }
    }, [keyNumber, dispatch]);

    const displayTitle = () => {
        switch (String(currentStep)) {
            case "1":
                return isClient ? designConfig.textFirstClient : designConfig.textFirst;
            case "2":
                return isClient ? designConfig.secondFirstClient : designConfig.secondFirst;
            case "3":
                return "🎉 Compte créé avec succès !";
            default:
                return isClient ? designConfig.textFirstClient : designConfig.textFirst;
        }
    };

    return (
        <div className={`w-full md:w-2/3 lg:w-3/5 relative flex flex-col bg-white border border-white/80 rounded-2xl shadow-[0_24px_48px_-16px_rgba(15,23,42,0.35)] overflow-hidden`}>
            {/* En-tête en dégradé aux couleurs de la marque, aligné sur TypeAccount */}
            <div
                className="relative overflow-hidden px-6 md:px-10 lg:px-12 pt-6 md:pt-8 pb-6 flex-shrink-0"
                style={{ background: `linear-gradient(135deg, ${primary} 0%, ${primary}F2 60%, ${primary}D9 100%)` }}
            >
                <div className="absolute -top-16 -left-16 w-48 h-48 rounded-full" style={{ background: 'rgba(255,255,255,0.12)' }} />
                <div className="absolute -bottom-20 -right-10 w-64 h-64 rounded-full" style={{ background: 'rgba(255,255,255,0.08)' }} />

                {/* Indicateur d'étape, remplace la barre de progression du haut de page */}
                <div className="relative flex items-center gap-3 mb-4">
                    <span className={`text-white text-xs font-bold tracking-widest uppercase ${classes.fontFamily}`}>
                        Étape {Math.min(currentStep, totalSteps)} sur {totalSteps}
                    </span>
                    <div className="flex items-center gap-1.5">
                        {Array.from({ length: totalSteps }).map((_, i) => (
                            <span
                                key={i}
                                className="h-1.5 rounded-full transition-all duration-500"
                                style={{
                                    width: i < currentStep ? 28 : 16,
                                    background: i < currentStep ? 'rgba(255,255,255,0.95)' : 'rgba(255,255,255,0.35)',
                                }}
                            />
                        ))}
                    </div>
                </div>

                <h1 className={`relative ${common.subtitle} text-white font-extrabold leading-tight ${classes.fontFamily} mb-2`}>
                    {displayTitle()}
                </h1>
                {handleGoogleSignUp && (
                    <div className="relative mt-4">
                        <ButtonField
                            label="S'inscrire avec Google"
                            variant="outlined"
                            className="font-bold p-3 w-full lg:w-1/2 bg-white"
                            onClick={handleGoogleSignUp}
                            disabled={loading}
                            loading={loading}
                            startIcon={<img
                                src={iconGoogle}
                                alt="Google Icon"
                                className="w-5 h-5 md:w-6 md:h-6 transition-transform duration-300 group-hover:scale-110"
                            />}
                        />
                    </div>
                )}
            </div>

            {/* Divider Section */}
            {handleGoogleSignUp && <FormSeparator className="px-6 md:px-10 lg:px-12 py-4 md:py-6 flex-shrink-0" children={text ?? 'ou alors en renseignant ces informations :'} />}

            <div className="flex-1 z-40 px-6 md:px-10 lg:px-12 py-6">
                {children}
            </div>

            <div className={`flex-shrink-0 px-6 md:px-10 lg:px-12 pt-4 pb-6 md:pb-8 border-t border-gray-200`}>
                <div className="flex justify-between items-center gap-4">
                    <ButtonField
                        label={btnText ?? "Suivant"}
                        variant="contained"
                        className="font-bold w-full py-3"
                        onClick={handleSubmit}
                        disabled={loading}
                        loading={loading}
                    />
                </div>
            </div>
            <div className={`${currentStep === 1 ? 'flex' : 'hidden'} justify-center items-center mx-auto gap-x-2 ${classes.text} font-bold pb-4`}>
                Vous avez un compte ?{" "}<strong onClick={()=>navigate("/")} className={`${classes.primaryText} cursor-pointer underline`}>Se connecter</strong>
            </div>
        </div>
    );
};

export default LayoutSteps;
